/**
 * Pasting contractor operations and rate cards — shared types and number
 * formatting for Production Entry, the Rates tab and the contractor bill.
 * The pricing itself happens on the API (api/src/lib/pasting.ts).
 */

export type PastingUnit = 'PER_1000' | 'PER_HOUR';

/**
 * BOX   a box type (Side Pasting, Inner Lock, Corrugation…) priced for the
 *       whole job: its steps and the pasting.
 * STEP  a step of that job — taken off the box price when not done.
 * EXTRA other work, entered on its own (Resorting, Waiting…).
 */
export type OperationKind = 'BOX' | 'STEP' | 'EXTRA';

export interface PastingOperation {
  id: number;
  name: string;
  unit: PastingUnit;
  kind: OperationKind;
  /** Box types: adds to the job's produced qty when pasted.  Otherwise billed only. */
  countsAsProduced: boolean;
  /** Billed-only work whose entries go back to Quality (Resorting). */
  needsQc?: boolean;
  legacyJobType: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface RateCardDto {
  id: number;
  effectiveFrom: string;
  notes: string;
  createdByName: string | null;
  createdOn: string | null;
  /** One rate per operation (per 1000, or per hour); null = none. */
  rates: { operationId: number; rate: number | null }[];
}

export interface RatesResponse {
  operations: PastingOperation[];
  cards: RateCardDto[];
  currentCardId: number | null;
}

/** 12,34,567 */
export const inr = (n: number, digits = 0) =>
  (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** ₹1,23,456.50 */
export const rupees = (n: number | null | undefined) => (n == null ? '—' : `₹${inr(n, 2)}`);

export const unitLabel = (u: PastingUnit | string | null | undefined) => (u === 'PER_HOUR' ? 'per hour' : 'per 1000');

/** An entry's steps as stored: '+11,-12,+13' (done / not done).  NULL = the whole job. */
export function parseSteps(steps: string | null | undefined): { id: number; done: boolean }[] {
  if (!steps) return [];
  return steps.split(',').flatMap((t) => {
    const m = /^([+-])(\d+)$/.exec(t.trim());
    return m ? [{ id: Number(m[2]), done: m[1] === '+' }] : [];
  });
}

/**
 * The rate an entry would be paid on a card — the same rule the API bills by
 * (api/src/lib/pasting.ts entryRate): a box type's price is shared equally
 * by its parts, every step on offer plus the pasting, and each part not done
 * takes its share off; anything else is paid its own rate.  null = no rate.
 */
export function workRate(
  card: RateCardDto | undefined,
  op: PastingOperation | undefined,
  steps: PastingOperation[],
  skipped: number[],
  pasted: boolean,
): number | null {
  if (!card || !op) return null;
  const whole = card.rates.find((x) => x.operationId === op.id)?.rate ?? null;
  if (op.kind !== 'BOX' || whole == null) return whole;
  const parts = steps.length + 1;
  const done = steps.filter((s) => !skipped.includes(s.id)).length + (pasted ? 1 : 0);
  return Math.round(((whole * done) / parts) * 100) / 100;
}
